<i18n>
{
  "en": {
    "title": "EcoStruxure Sustainability Report 2017-2018",
    "description": "EcoStruxure is Schneider Electric’s IoT-enabled solution for increasing operational and energy efficiency. Learn how EcoStruxure brings sustainability through connected products, edge control, and smart apps, analytics, and services.",
    "sustainable": {
      "section": "Sustainable Solutions",
      "title": "EcoStruxure Innovations",
      "ecostruxure": {
        "quote": {
          "para": "Digitization is at the core of Schneider’s DNA. We connect people and assets to drive efficiency, safety, and reliability.",
          "title": "Executive Vice President, Information Systems, Schneider Electric"
        },
        "iot": {
          "title": "The Internet of Things for a sustainable tomorrow",
          "para": "The EcoStruxure<sup>&trade;</sup> platform and architecture is our IoT-enabled solution that increases operational and energy efficiency. EcoStruxure solutions have been deployed in 480,000+ sites, with the support of 20,000+ system integrators and developers, connecting over 1.6 million assets under management through 40+ digital services."
        },
        "table": {
          "title": "EcoStruxure provides Innovation At Every Level:",
          "row1": {
            "cell1": {
              "title": "Level 3: Apps, Analytics, and Services"
            },
            "cell2": {
              "p1": "EcoStruxure leverages the cloud for computing power, aggregation of data and one data pool, machine learning models / new functionalities, and digital services. Thanks to analytics and artificial intelligence, data can take on meaning across a business (from shop floor to top floor), allowing a company to scrutinize energy use, for example, and to make more targeted decisions about how to prioritize energy projects."
            }
          },
          "row2": {
            "cell1": {
              "title": "Level 2: Edge Control"
            },
            "cell2": {
              "p1": "Mission-critical environments need local control of devices at the edge and reliable access to data from operational technology (OT) devices. Advancements such as artificial intelligence enable analytics close to the source of data, reducing latency while protecting safety, productivity, and uptime."
            }
          },
          "row3": {
            "cell1": {
              "title": "Level 1: Connected Products"
            },
            "cell2": {
              "p1": "The Internet of Things starts with the best things. EcoStruxure is based on smart, connected products with open integration to third-party products. Embedding sensors in devices opens a vast window to what’s happening both inside that product — from its energy efficiency to its performance and within its environment, too. Through internet connectivity, IoT enables all these connected “things” to talk to each other."
            }
          }
        },
        "sixDomains": {
          "title": "Through six domains of expertise:",
          "one": {
            "title": "EcoStruxure Building",
            "para": "From design, through integration, to commissioning, EcoStruxure Building brings best-in-class engineering efficiency to buildings, combined with asset and energy performance services."
          },
          "two": {
            "title": "EcoStruxure Plant",
            "para": "IoT-enabled, future-proof solutions for improved operational profitability in a measurable, safe, and sustainable way."
          },
          "three": {
            "title": "EcoStruxure Grid",
            "para": "Seamless local production of distributed energy resources and integration at the grid edge, bridging demand and supply via energy flexibility based on analytics and increasing grid efficiency for sustainable networks."
          },
          "four": {
            "title": "EcoStruxure IT",
            "para": "Connected products and remote, digital services to ensure that data center physical infrastructure can adapt quickly to support both future demand driven by IoT and growth — in the cloud and at the edge — without ever compromising availability or operational efficiency."
          },
          "five": {
            "title": "EcoStruxure Power",
            "para": "Safe, highly available, and energy-efficient electrical distribution systems for low- and medium-voltage architectures to enhance connectivity, real-time operational reliability, and smart analytics."
          },
          "six": {
            "title": "EcoStruxure Machine",
            "para": "Innovative machine solutions and smart automation technologies for safer, connected, flexible, and more efficient machines."
          }
        },
        "benefits": {
          "title": "The benefits of EcoStruxure:",
          "listItem1": "Maximize energy efficiency and sustainability through smarter systems and real-time, data-driven decisions",
          "listItem2": "Optimize asset availability and performance through predictive analytics and proactive maintenance",
          "listItem3": "Enable smart, productive, and profitable operations through the reduction of waste and downtime",
          "listItem4": "Leverage mobile insights and proactive risk-mitigation through simulation, situational awareness, and digitization",
          "listItem5": "Lower CapEx and OpEx across the digital life cycle by using “digital twin” capabilities to design, build, operate, and maintain systems and infrastructure",
          "ctap": "Discover how EcoStruxure delivers efficiency and sustainability.",
          "cta1": "Learn more",
          "cta2": "Customer stories"
        },
        "statement": {
          "title": "\"A transformative platform that changes the core way that data centers are monitored and managed.\"",
          "caption": "&mdash; International Data Corporation, 2017"
        },
        "statement2": {
          "title": "Also see: Highlights: EcoStruxure’s impact for our customers",
          "cta": "See the highlights"
        },
        "4cols1": {
          "title": "Across four key markets:",
          "cell1": {
            "cta": "Building"
          },
          "cell2": {
            "cta": "Data Center"
          },
          "cell3": {
            "cta": "Industry"
          },
          "cell4": {
            "cta": "Infrastructure"
          }
        },
        "vid1": {
          "title": "EcoStruxure Innovation Explained",
          "desc": "Cyril Perducat, Executive Vice President, IoT & Digital Transformation, Schneider Electric"
        }
      }
    }
  },
  "fr": {
    "title": "EcoStruxure Sustainability Report 2017-2018",
    "description": "EcoStruxure is Schneider Electric’s IoT-enabled solution for increasing operational and energy efficiency. Learn how EcoStruxure brings sustainability through connected products, edge control, and smart apps, analytics, and services.",
    "sustainable": {
      "section": "Solutions Durables",
      "title": "Les Innovations EcoStruxure",
      "ecostruxure": {
        "quote": {
          "para": "Le numérique  est au cœur de l’ADN de Schneider Electric. Nous connectons les personnes et les actifs pour plus d’efficacité, de sécurité et de fiabilité.",
          "title": "Directeur général, Systèmes d'information, Schneider Electric"
        },
        "iot": {
          "title": "L’Internet des objets pour un avenir durable",
          "para": "Notre solution d’IdO, EcoStruxure, améliore l’efficacité opérationnelle et énergétique. Elle a été déployée dans plus de 480 000 installations avec le concours de 20 000 intégrateurs système et développeurs. Toutes nos nouvelles offres sont compatibles EcoStruxure, et en 2017, on comptait 1,6 million d'actifs gérés par l’intermédiaire de 40 services numériques disponibles sur le marché."
        },
        "table": {
          "title": "EcoStruxure est porteur d’innovation à tous les niveaux",
          "row1": {
            "cell1": {
              "title": "Niveau 3 : Applications, outils d’analyse et services"
            },
            "cell2": {
              "p1": "EcoStruxure mise sur le cloud pour sa puissance de calcul et pour sa capacité d’agrégation des données en un seul et même endroit, ainsi que sur les modèles d'apprentissage automatique et les nouvelles fonctionnalités qui en découlent, sans oublier les services numériques. L’analyse des données et l’intelligence artificielle contribuent à fournir des informations à tous les niveaux (de l'échelon de la production à celui de la direction), permettant par exemple à une entreprise donnée de surveiller sa consommation d'énergie et de prendre des décisions plus ciblées quant au degré de priorité des projets énergétiques."
            }
          },
          "row2": {
            "cell1": {
              "title": "Niveau 2 : Contrôle local (Edge Control)"
            },
            "cell2": {
              "p1": "Dans les environnements critiques pour le bon déroulement de la mission, il est nécessaire de mettre en place un contrôle local des appareils à la périphérie, et d'assurer un accès fiable aux données issues des appareils opérationnels. Des progrès tels que l’intelligence artificielle permettent de conduire des analyses proches de la source des données, et donc de réduire le temps de latence tout en assurant la sécurité, la productivité et la disponibilité des systèmes."
            }
          },
          "row3": {
            "cell1": {
              "title": "Niveau 1 : Produits connectés"
            },
            "cell2": {
              "p1": "EcoStruxure repose sur des produits intelligents et connectés, compatibles avec des produits tiers. L’intégration de capteurs aux objets ouvre un vaste champ d'étude, qui touche à la fois au fonctionnement interne du produit &mdash; de son efficacité énergétique à ses performances &mdash; et à son environnement. Grâce à une liaison assurée par Internet, l’IdO permet aux objets connectés « d’interagir »."
            }
          }
        },
        "sixDomains": {
          "title": "Six domaines d'expertise",
          "one": {
            "title": "EcoStruxure Bâtiment",
            "para": "De la phase de conception à l'intégration et à la commande, EcoStruxure Building apporte la meilleure efficacité pour l'ingénierie des bâtiments en combinant produits connectés et services de performance énergétique."
          },
          "two": {
            "title": "EcoStruxure Usine",
            "para": "Des solutions durables et compatibles IdO pour améliorer la rentabilité opérationnelle d'une façon mesurable, sûre et durable."
          },
          "three": {
            "title": "EcoStruxure Réseau",
            "para": "Production locale transparente de ressources énergétiques distribuées, intégration à la périphérie du réseau, adaptation de l'offre à la demande grâce à une flexibilité énergétique reposant sur l'analyse des données et l'amélioration de l'efficacité du réseau."
          },
          "four": {
            "title": "EcoStruxure IT",
            "para": "Produits connectés et services digitaux à distance propres à garantir que l'infrastructure physique des centres de données s'adapte rapidement pour répondre à la fois à la demande future générée par l'IdO et à la croissance, sur le cloud et dans les réseaux périphériques, sans jamais compromettre la disponibilité ou l'efficacité opérationnelle."
          },
          "five": {
            "title": "EcoStruxure Énergie",
            "para": "Systèmes d’alimentation électrique plus sûrs, plus fiables et plus efficaces destinés aux architectures basse tension et moyenne tension, pour une meilleure connectivité, un fonctionnement en temps réel et une analyse intelligente."
          },
          "six": {
            "title": "EcoStruxure Machine",
            "para": "Solutions innovantes destinées aux machines et technologies d’automatisation intelligentes pour des machines connectées, flexibles, plus sûres et plus efficaces."
          }
        },
        "benefits": {
          "title": "Avantages d’EcoStruxure",
          "listItem1": "<strong>Maximiser l’efficacité énergétique et la durabilité</strong> via des systèmes plus intelligents et des décisions prises en temps réel et fondées sur les données",
          "listItem2": "<strong>Optimiser la disponibilité et la performance des actifs</strong> via une analyse prédictive et une maintenance proactive",
          "listItem3": "<strong>Mener des activités intelligentes, productives et rentables</strong> via la diminution du gaspillage et des dysfonctionnements",
          "listItem4": "<strong>Capitaliser sur les technologies mobiles et la maîtrise proactive des risques</strong> via la simulation, la connaissance situationnelle et la numérisation",
          "listItem5": "<strong>Réduire les dépenses d’investissement et les dépenses opérationnelles</strong> tout au long du cycle digital grâce à la technologie du \"digital twin\" pour la création, la construction et la maintenance des systèmes et des infrastructures",
          "ctap": "Découvrir comment EcoStruxure assure efficacité et durabilité",
          "cta1": "En savoir plus sur EcoStruxure",
          "cta2": "Nos clients racontent"
        },
        "statement": {
          "title": "« Une plateforme de transformation qui bouleverse le pilotage et la gestion des centres de données. »",
          "caption": "&mdash; International Data Corporation, 2017"
        },
        "4cols1": {
          "title": "Sur nos quatre grands marchés :",
          "cell1": {
            "cta": "Bâtiment"
          },
          "cell2": {
            "cta": "Centre de données"
          },
          "cell3": {
            "cta": "Industrie"
          },
          "cell4": {
            "cta": "Réseau"
          }
        },
        "vid1": {
          "title": "Tout savoir sur EcoStruxure",
          "desc": "Cyril Perducat, Directeur Digital, Schneider Electric"
        }
      }
    }
  }
}
</i18n>

<!-- ,
"statement": {
  "title": "Also see: Highlights: EcoStruxure’s impact for our customers",
  "cta": "See the highlights"
} -->
<template>
  <div id="">
    <Hero :loResSrc="require('../../assets/img/sustainable/sustainable-solutions-ecostruxure-hero-lowres.jpg')" :hiResSrc="require('../../assets/img/sustainable/sustainable-solutions-ecostruxure-hero.jpg')" chapter="02" :section="$t('sustainable.section')" :title="$t('sustainable.title')" :grayBG="true" />

    <section class="">
      <Quote :photo="require('../../assets/img/sustainable/sustainable-solutions-ecostruxure-quote.jpg')" name="Hervé Coureil" :title="$t('sustainable.ecostruxure.quote.title')">
        <div v-html="$t('sustainable.ecostruxure.quote.para')"></div>
      </Quote>
    </section>

    <section class="m-b-120 l-section-pad l-bg-white">
      <YouTubeVideo videoId="NKbhg9kJ784" :title="$t('sustainable.ecostruxure.vid1.title')" :description="$t('sustainable.ecostruxure.vid1.desc')" :preview="require('@/assets/img/thumbnails/SSDHighlightVideo_CyrilPerducat_Still.jpg')" />
    </section>

    <section class="m-b-120 m-t-120">
      <Txt>
        <h2 id="ecostruxureiot" v-html="$t('sustainable.ecostruxure.iot.title')"/>
        <p v-html="$t('sustainable.ecostruxure.iot.para')"/>
      </Txt>
    </section>

    <section class="l-bg-white l-section-pad">
      <Table columns="3">
        <h2 id="ecostruxureinnovationateverylevel" slot="above" v-html="$t('sustainable.ecostruxure.table.title')"></h2>
        <TableRow>
          <TableCell bgc="#3dcd58">
            <h3 id="appsanalyticsandservices" class="t-white" v-html="$t('sustainable.ecostruxure.table.row1.cell1.title')"></h3>
          </TableCell>
          <TableCell :bgimg="require('@/assets/img/sustainable/_G9A5959.jpg')" class="no-overlay">
          </TableCell>
          <TableCell>
            <p class="t-chartbody" v-html="$t('sustainable.ecostruxure.table.row1.cell2.p1')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <h3 id="edgecontrol" v-html="$t('sustainable.ecostruxure.table.row2.cell1.title')"></h3>
          </TableCell>
          <TableCell :bgimg="require('@/assets/img/sustainable/998-20237451_Easergy-P3_GMA_US_Social-Media_1200x1200_V2.jpg')" class="no-overlay">
          </TableCell>
          <TableCell bgc="#626469">
            <p class="t-chartbody t-white" v-html="$t('sustainable.ecostruxure.table.row2.cell2.p1')"></p>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell bgc="#3dcd58">
            <h3 id="connectedproducts" class="t-white" v-html="$t('sustainable.ecostruxure.table.row3.cell1.title')"></h3>
          </TableCell>
          <TableCell :bgimg="require('@/assets/img/sustainable/_82A3641.jpg')" class="no-overlay">
          </TableCell>
          <TableCell>
            <p class="t-chartbody" v-html="$t('sustainable.ecostruxure.table.row3.cell2.p1')"></p>
          </TableCell>
        </TableRow>
      </Table>
    </section>

    <section class="m-t-120 m-b-120">
      <div class="l-content">
        <div class="row">
          <div class="col-xs-16 col-xs-offset-1">
            <SvgContainer v-if="$i18n.locale === 'en'" >
              <SSEcoIG class="scaling-svg" />
            </SvgContainer>
            <SvgContainer v-if="$i18n.locale === 'fr'" >
              <SSEcoIGFR class="scaling-svg" />
            </SvgContainer>
          </div>
        </div>
      </div>
    </section>

    <section class="m-t-120 m-b-120 l-section-pad l-bg-white">
      <Table>
        <div slot="above"><h2 id="sixdomains" v-html="$t('sustainable.ecostruxure.sixDomains.title')"/></div>
        <TableRow>
          <TableCell :bgimg="require('@/assets/img/sustainable/SE_ORLANDO_0834.jpg')">
            <h3 id="ecostruxurepowerdomain" class="t-white" v-html="$t('sustainable.ecostruxure.sixDomains.five.title')"/>
          </TableCell>
          <TableCell>
            <p class="t-chartbody" v-html="$t('sustainable.ecostruxure.sixDomains.five.para')" />
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <h3 id="ecostruxurebuildingdomain" v-html="$t('sustainable.ecostruxure.sixDomains.one.title')"/>
          </TableCell>
          <TableCell :bgimg="require('@/assets/img/sustainable/GettyImages-555084645.jpg')">
            <p class="t-chartbody t-white" v-html="$t('sustainable.ecostruxure.sixDomains.one.para')"/>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell :bgimg="require('@/assets/img/sustainable/GettyImages-607283026.jpg')">
            <h3 id="ecostruxuregriddomain" class="t-white" v-html="$t('sustainable.ecostruxure.sixDomains.three.title')"/>
          </TableCell>
          <TableCell>
            <p class="t-chartbody" v-html="$t('sustainable.ecostruxure.sixDomains.three.para')"/>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <h3 id="ecostruxureitdomain" v-html="$t('sustainable.ecostruxure.sixDomains.four.title')"/>
          </TableCell>
          <TableCell :bgimg="require('@/assets/img/sustainable/GettyImages-580501693.jpg')">
            <p class="t-chartbody t-white" v-html="$t('sustainable.ecostruxure.sixDomains.four.para')" />
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell :bgimg="require('@/assets/img/sustainable/169264319.jpg')">
            <h3 id="ecostruxuremachinedomain" class="t-white" v-html="$t('sustainable.ecostruxure.sixDomains.six.title')"/>
          </TableCell>
          <TableCell>
            <p class="t-chartbody" v-html="$t('sustainable.ecostruxure.sixDomains.six.para')"/>
          </TableCell>
        </TableRow>

        <TableRow>
          <TableCell>
            <h3 id="ecostruxureplantdomain" v-html="$t('sustainable.ecostruxure.sixDomains.two.title')"/>
          </TableCell>
          <TableCell :bgimg="require('@/assets/img/sustainable/GettyImages-530293155.jpg')">
            <p class="t-chartbody t-white" v-html="$t('sustainable.ecostruxure.sixDomains.two.para')"/>
          </TableCell>
        </TableRow>
      </Table>
    </section>

    <section class="m-t-120 m-b-120">
      <FourCols>
        <div slot="above"><h2 id="fourkeymarkets" v-html="$t('sustainable.ecostruxure.4cols1.title')"/></div>
        <FourColsItem>
          <BuildingIcon class="m-b-32" />
          <router-link class="cta-button-route" :to="{ name: 'OurCustomers_Building' }" v-html="$t('sustainable.ecostruxure.4cols1.cell1.cta')"></router-link>
        </FourColsItem>

        <FourColsItem>
          <DataCenterIcon class="m-b-32" />
          <router-link class="cta-button-route" :to="{ name: 'OurCustomers_DataCenter' }" v-html="$t('sustainable.ecostruxure.4cols1.cell2.cta')"></router-link>
        </FourColsItem>

        <FourColsItem>
          <IndustryIcon class="m-b-32" />
          <router-link class="cta-button-route" :to="{ name: 'OurCustomers_Industry' }" v-html="$t('sustainable.ecostruxure.4cols1.cell3.cta')"></router-link>
        </FourColsItem>

        <FourColsItem>
          <InfrastructureIcon class="m-b-32" />
          <router-link class="cta-button-route" :to="{ name: 'OurCustomers_Infrastructure' }" v-html="$t('sustainable.ecostruxure.4cols1.cell4.cta')"></router-link>
        </FourColsItem>

      </FourCols>
    </section>

    <section class="m-t-120 m-b-120 l-section-pad l-bg-white">
      <Txt>
        <h2 id="ecostruxurebenefits" v-html="$t('sustainable.ecostruxure.benefits.title')"></h2>
        <ul>
          <li v-html="$t('sustainable.ecostruxure.benefits.listItem1')"></li>
          <li v-html="$t('sustainable.ecostruxure.benefits.listItem2')"></li>
          <li v-html="$t('sustainable.ecostruxure.benefits.listItem3')"></li>
          <li v-html="$t('sustainable.ecostruxure.benefits.listItem4')"></li>
          <li v-html="$t('sustainable.ecostruxure.benefits.listItem5')"></li>
        </ul>
        <p v-html="$t('sustainable.ecostruxure.benefits.ctap')"></p>
        <CTA class="m-r-14 m-b-14" target="_blank" :buttonText="$t('sustainable.ecostruxure.benefits.cta1')" href="https://www.schneider-electric.com/en/work/campaign/innovation/overview.jsp"></CTA>
        <router-link class="cta-button-route" v-html="$t('sustainable.ecostruxure.benefits.cta2')" :to="{name: 'OurCustomers_Overview', params: {lang: $route.params.lang }}"></router-link>
      </Txt>
    </section>

    <section class="m-b-120 m-t-120">
     <Statement>
       <h3 v-html="$t('sustainable.ecostruxure.statement.title')"/>
       <p v-html="$t('sustainable.ecostruxure.statement.caption')"></p>
     </Statement>
   </section>

   <div class="l-content">
      <FooterNav
        nextRoute="SustainableSolutions_EnergyServices"
        prevRoute="SustainableSolutions_Overview" />
    </div>

  </div>
</template>

<script>
import FooterNav from '@/components/FooterNav'
import Hero from '@/components/Hero'
import Quote from '@/components/Quote'
import Statement from '@/components/Statement'
import FourCols from '@/components/FourCols'
import FourColsItem from '@/components/FourColsItem'
import CTA from '@/components/CTA'
import Txt from '@/components/Txt'
import Table from '@/components/Table'
import TableRow from '@/components/TableRow'
import TableCell from '@/components/TableCell'
import SvgContainer from '@/components/SvgContainer'
import YouTubeVideo from '@/components/YouTubeVideo'

import SSEcoIG from '@/assets/img/ig/sustainable-solutions-ecostruxure.svg'
import SSEcoIGFR from '@/assets/img/ig/sustainable-solutions-ecostruxure_fr.svg'
import BuildingIcon from '@/assets/img/sustainable/building.svg'
import DataCenterIcon from '@/assets/img/sustainable/data-center.svg'
import IndustryIcon from '@/assets/img/sustainable/industry.svg'
import InfrastructureIcon from '@/assets/img/sustainable/infrastructure.svg'

export default {
  name: 'SustainableSolutions_Ecostruxure',
  metaInfo () {
    return {
      title: this.$t('title'),
      meta: [
        { name: 'description', content: this.$t('description') }
      ]
    }
  },
  mounted () {
    if (window.location.hash.substr(1).length > 0) {
      this.$nextTick(function () {
        document.getElementById(window.location.hash.substr(1)).scrollIntoView()
      })
    }
  },
  components: {
    FooterNav,
    Hero,
    Quote,
    Statement,
    FourCols,
    FourColsItem,
    CTA,
    Txt,
    Table,
    TableRow,
    TableCell,
    SvgContainer,
    SSEcoIG,
    SSEcoIGFR,
    BuildingIcon,
    DataCenterIcon,
    IndustryIcon,
    InfrastructureIcon,
    YouTubeVideo
  }
}
</script>



// WEBPACK FOOTER //
// src/pages/sustainable-solutions/ecostruxure.vue